import { createGlobalStyle } from 'styled-components';

export const GlobalStyle = createGlobalStyle`
  /* Mode-dependent design tokens — mirrors the v15 kit tokens.css.
     Dark is the default; [data-theme="light"] (set on <html>) overrides the light set.
     The dark set is also applied to any nested [data-theme="dark"] element so a subtree can opt
     back into dark inside a light page (a "dark island" — e.g. the auth brand panel, which is a
     dark hero in both modes). */
  :root,
  [data-theme='dark'] {
    /* Native UI (scrollbars, form controls) follows the mode — no white scrollbars on dark. */
    color-scheme: dark;
    --bg-base: #04050D;
    --bg-deep: #070A18;
    --surface-1: #0A0E20;
    --surface-2: #0E1330;
    --surface-3: #141A3E;
    --surface-glass: rgba(20, 24, 56, 0.55);

    --stroke-soft: rgba(141, 133, 251, 0.14);
    --stroke-med: rgba(141, 133, 251, 0.26);
    --stroke-strong: rgba(141, 133, 251, 0.45);
    --stroke-cyan: rgba(125, 229, 243, 0.35);

    --text-primary: #F6F6F6;
    --text-secondary: #A9B0D6;
    --text-tertiary: #6C7299;
    --text-disabled: #474D6E;

    --buy-green: #2BD899;
    --buy-green-bg: rgba(43, 216, 153, 0.10);
    --sell-red: #FF4D6A;
    --sell-red-bg: rgba(255, 77, 106, 0.10);
    --bullish: #3DDC97;
    --bearish: #FF5470;
    --warning: #FFC65C;
    --warning-bg: rgba(255, 198, 92, 0.12);
    --trade-teal: #1FA3C4;

    --grad-hero-text: linear-gradient(180deg, #FFFFFF 25%, #B9B4FD 65%, #8D85FB 100%);
    --grad-hero-silver: linear-gradient(180deg, #FFFFFF 20%, #C9CDE8 100%);
    --grad-card: linear-gradient(165deg, #10142E 0%, #070A18 70%);
    --grad-hero-card: linear-gradient(180deg, #131736 0%, #06081A 55%, #04050D 100%);

    --glow-indigo: 0 0 28px rgba(141, 133, 251, 0.45);
    --glow-indigo-lg: 0 0 60px rgba(141, 133, 251, 0.55);
    --glow-cobalt: 0 8px 28px rgba(70, 100, 249, 0.45);
    --glow-cyan: 0 0 24px rgba(125, 229, 243, 0.35);
    --glow-teal: 0 8px 24px rgba(31, 163, 196, 0.40);
    --glow-danger: 0 8px 28px rgba(255, 77, 106, 0.45);
    --glow-sell: 0 0 18px rgba(255, 77, 106, 0.22);
    --glow-buy: 0 0 18px rgba(43, 216, 153, 0.22);
    --shadow-card: 0 18px 48px rgba(0, 0, 0, 0.55);

    /* On-surface text/icon accent (brand-core hue on dark → cobalt/teal on light for AA) */
    --accent-ink: #8D85FB;
    --accent-ink-cyan: #7DE5F3;
    --accent-wash: rgba(141, 133, 251, 0.08);
    --accent-wash-strong: rgba(141, 133, 251, 0.12);

    /* Sticky-header glass + content skeletons */
    --header-bg: rgba(4, 5, 13, 0.88);
    --search-bg: rgba(10, 14, 32, 0.85);
    --skeleton-bg: #0E1330;
    --skeleton-fg: #141A3E;

    /* Connection status (AQT-888): banner states, stale quotes, paused trading controls */
    --conn-warn: #F5B83D;
    --conn-reconnecting-bg: #2A2110;
    --conn-reconnecting-border: #4A3A16;
    --conn-reconnecting-detail: #E6D3A6;
    --conn-offline-bg: #1E2340;
    --conn-stroke: #2E3560;
    --conn-recovered-bg: #0D2A20;
    --conn-recovered-border: #16493A;
    --conn-recovered-detail: #9FD9C2;
    --conn-stale-text: #8990B8;
    --conn-chip-disabled-border: #2A2F50;
    --conn-chip-disabled-text: #6E7499;
    --conn-sell-disabled-bg: #24161E;
    --conn-sell-disabled-text: #8A6272;
    --conn-buy-disabled-bg: #10241E;
    --conn-buy-disabled-text: #5E8A7A;
  }

  :root[data-theme='light'] {
    color-scheme: light;
    --bg-base: #F6F6F6;
    --bg-deep: #ECEEF3;
    --surface-1: #FFFFFF;
    --surface-2: #FAFBFE;
    --surface-3: #F1F3FB;
    --surface-glass: rgba(255, 255, 255, 0.70);

    --stroke-soft: rgba(16, 22, 38, 0.08);
    --stroke-med: rgba(16, 22, 38, 0.14);
    --stroke-strong: rgba(70, 100, 249, 0.45);
    --stroke-cyan: rgba(31, 163, 196, 0.45);

    --text-primary: #101626;
    --text-secondary: #4A5170;
    --text-tertiary: #696F8E;
    --text-disabled: #AEB3C9;

    --buy-green: #098257;
    --buy-green-bg: rgba(9, 130, 87, 0.12);
    --sell-red: #E02947;
    --sell-red-bg: rgba(224, 41, 71, 0.12);
    --bullish: #098257;
    --bearish: #E02947;
    --warning: #A8690E;
    --warning-bg: rgba(168, 105, 14, 0.12);
    --trade-teal: #157E9B;

    --grad-hero-text: linear-gradient(180deg, #1B2150 0%, #4664F9 100%);
    --grad-hero-silver: linear-gradient(180deg, #2A2F4A 0%, #5A6080 100%);
    --grad-card: linear-gradient(165deg, #FFFFFF 0%, #F4F5F8 70%);
    --grad-hero-card: linear-gradient(180deg, #FFFFFF 0%, #F4F5F8 55%, #ECEEF3 100%);

    --glow-indigo: 0 2px 12px rgba(70, 100, 249, 0.18);
    --glow-indigo-lg: 0 10px 30px rgba(70, 100, 249, 0.20);
    --glow-cobalt: 0 8px 24px rgba(70, 100, 249, 0.22);
    --glow-cyan: 0 0 0 1px rgba(31, 163, 196, 0.30);
    --glow-teal: 0 8px 20px rgba(31, 163, 196, 0.22);
    --glow-danger: 0 8px 24px rgba(224, 41, 71, 0.22);
    --glow-sell: 0 4px 14px rgba(224, 41, 71, 0.18);
    --glow-buy: 0 4px 14px rgba(9, 130, 87, 0.18);
    --shadow-card: 0 12px 32px rgba(16, 22, 38, 0.10);

    --accent-ink: #4664F9;
    --accent-ink-cyan: #157E9B;
    --accent-wash: rgba(70, 100, 249, 0.06);
    --accent-wash-strong: rgba(70, 100, 249, 0.10);

    --header-bg: rgba(246, 246, 246, 0.85);
    --search-bg: #FFFFFF;
    --skeleton-bg: #E7EAF2;
    --skeleton-fg: #F1F3F9;

    --conn-warn: #A8690E;
    --conn-reconnecting-bg: #FEF6EC;
    --conn-reconnecting-border: #F1DDB8;
    --conn-reconnecting-detail: #6B4E1A;
    --conn-offline-bg: #EEF0F8;
    --conn-stroke: #D5D9EC;
    --conn-recovered-bg: #EAF7F1;
    --conn-recovered-border: #BFE6D4;
    --conn-recovered-detail: #2E6B55;
    --conn-stale-text: #7A809E;
    --conn-chip-disabled-border: #E1E4EF;
    --conn-chip-disabled-text: #AEB3C9;
    --conn-sell-disabled-bg: #F8ECEE;
    --conn-sell-disabled-text: #C28D99;
    --conn-buy-disabled-bg: #E8F4EF;
    --conn-buy-disabled-text: #7FAA99;
  }

  /* Always reserve the scrollbar gutter so the page width (and split-screen layouts) don't
     shift when a scrollbar appears/disappears between routes/steps. */
  html {
    scrollbar-gutter: stable;
    overflow-x: clip;
  }

  /* Dark uses neon radial glows for ambient depth; light is a flat alabaster surface
     (matches the kit — elevation comes from card shadows, not background washes). */
  body {
    background:
      radial-gradient(1100px 520px at 8% -10%, rgba(70, 100, 249, 0.16), transparent 60%),
      radial-gradient(900px 480px at 96% 6%, rgba(141, 133, 251, 0.12), transparent 60%),
      radial-gradient(700px 400px at 60% 110%, rgba(125, 229, 243, 0.06), transparent 60%),
      ${({ theme }) => theme.brand.bgBase};
    color: ${({ theme }) => theme.brand.textPrimary};
    /* Clip the off-canvas mobile nav drawer (position: fixed, parked off-screen) so it
       doesn't add horizontal page scroll. clip (not hidden) leaves overflow-y visible,
       so the sticky header keeps working. */
    overflow-x: clip;
  }
  html[data-theme='light'] body {
    background: ${({ theme }) => theme.brand.bgBase};
  }
  ::selection {
    background: rgba(141, 133, 251, 0.35);
  }

    /* using the previous version to override the new version (!danger) */
  .str-chat__channel-search {
    display: flex;
  }

  str-chat__channel-search {
    padding: var(--md-p) var(--md-p) 0;
    display: flex;
    align-items: center;
    position: relative;
    background: var(--grey-gainsboro);
  }
  .str-chat__channel-search input {
    flex: 1;
    background: var(--grey-whisper);
    border: 1px solid transparent;
    outline: none;
    height: 30px;
    border-radius: var(--border-radius);
    color: var(--black);
    font-size: var(--md-font);
    padding: var(--sm-p);
  }
  .str-chat__channel-search input::placeholder {
    color: var(--grey);
  }
  .str-chat__channel-search input:focus {
    background: var(--white);
    border: 1px solid var(--primary-color);
    box-shadow: 0 0 0 2px var(--primary-color-faded);
    transition: all 0.2s ease-in-out;
  }
  .str-chat__channel-search-container {
    position: absolute;
    width: 300px;
    z-index: ${props => props.theme.zIndex.chatSearchContainer};
    padding: 8px;
  }
  .str-chat__channel-search-container-searching {
    padding-left: 8px;
  }
  .str-chat__channel-search-container-empty {
    padding-left: 8px;
  }
  .str-chat__channel-search-container .str-chat__channel-search-result {
    display: flex;
    align-items: center;
    border-radius: var(--border-radius-md);
    height: 40px;
    cursor: pointer;
    padding-left: 8px;
    width: 100%;
    border: none;
    background: transparent;
  }
  .str-chat__channel-search-container .str-chat__channel-search-result:hover {
    font-weight: var(--font-weight-bold);
    transition: all 0.2s ease-in-out;
  }
  .str-chat__channel-search-result.focused {
    font-weight: var(--font-weight-bold);
  }
  .str-chat__channel-search-container.popup {
    border-radius: var(--border-radius-md);
    background: var(--white-smoke);
    left: 300px;
    top: 20px;
    box-shadow: 0 7px 9px 0 var(--border), 0 1px 0 0 var(--border);
    border: 1px solid var(--border);
  }
  .str-chat__channel-search-container.popup .str-chat__channel-search-result:hover {
    background: var(--light-grey);
  }
  .str-chat__channel-search-container.popup .str-chat__channel-search-result.focused {
    background: var(--light-grey);
  }
  .str-chat__channel-search-container.inline {
    top: 64px;
    right: 0;
    background: var(--grey-gainsboro);
    height: calc(100vh - 60px);
  }
  .str-chat__channel-search-container.inline .str-chat__channel-search-result:hover {
    background: var(--white);
  }
  .str-chat__channel-search-container.inline .str-chat__channel-search-result.focused {
    background: var(--white);
  }
  .dark.str-chat .str-chat__channel-search,
  .dark.str-chat .str-chat__channel-search-container.inline {
    background: var(--dark-grey);
    color: var(--white);
  }
  .dark.str-chat .str-chat__channel-search input,
  .dark.str-chat .str-chat__channel-search-container.inline input {
    background: var(--grey-gainsboro);
  }
  .dark.str-chat .str-chat__channel-search .str-chat__channel-search-result,
  .dark.str-chat .str-chat__channel-search-container.inline .str-chat__channel-search-result {
    color: var(--white);
  }
  .dark.str-chat .str-chat__channel-search .str-chat__channel-search-result:hover,
  .dark.str-chat .str-chat__channel-search-container.inline .str-chat__channel-search-result:hover {
    background: var(--white5);
  }

  /* my custom */

  .str-chat.messaging {
    background-color: ${props => props.theme.brand.bgBase} !important;
  }
  /* stream applies a .light theme class whose .str-chat__list rule is white and
     out-specifies single-class overrides; flip it globally (main chat, floating, pinned) */
  .str-chat__list,
  .str-chat__list-notifications {
    background: ${props => props.theme.brand.bgBase} !important;
  }
  .str-chat__li--middle .message-sender-name,
  .str-chat__li--bottom .message-sender-name,
  .grp-same-author .message-sender-name {
    display: none !important;
  }
  /* stream pads list items by group position (single = top+bottom, top = top,
     bottom = bottom) which makes gaps uneven; zero it and let the margins below
     be the single source of vertical spacing */
  .str-chat__li--top,
  .str-chat__li--middle,
  .str-chat__li--bottom,
  .str-chat__li--single {
    padding-top: 0;
    padding-bottom: 0;
  }
  /* consistent gap before each new group (new sender / fresh run) */
  .str-chat__li--top,
  .str-chat__li--single {
    margin-top: 16px;
  }
  /* same author, just split by a time gap — keep the same tight gap as a run,
     so a shown timestamp doesn't enlarge the space; only a sender change spaces out */
  .str-chat__li--top.grp-same-author,
  .str-chat__li--single.grp-same-author {
    margin-top: 4px;
  }
  /* tight gap between a sender's own consecutive messages */
  .str-chat__li--middle,
  .str-chat__li--bottom {
    margin-top: 4px;
  }
  /* timestamp belongs once per group (its last message) — drop the per-message
     data row on earlier messages in a run so consecutive bubbles sit tight */
  .str-chat__li--top .str-chat__message-data,
  .str-chat__li--middle .str-chat__message-data {
    display: none;
  }


  .str-chat__message-attachment--image img {
    width: inherit;
    height: inherit;
  }

  .str-chat__message-attachment-card:not(.str-chat__message-attachment-card--giphy) .str-chat__message-attachment-card--header img {
    width: 100%;
    height: 100%;
  }

  .str-chat__modal__inner {
    max-width: 50vw;
  }

  .str-chat__modal__inner.fullscreen {
    position: fixed;
    top: 0;
    left: 0;
    z-index: ${props => props.theme.zIndex.chatInnerModal};
    max-width: 100vw;
    max-height: 100vh;
    width: 100vw;
    height: 100vh;
    display: flex;
    justify-content: center;
    align-items: center;
  }

  /* Chat modal card — stream v1 CSS renders it white; brand it (dark card in dark,
     light in light). Excludes the fullscreen image gallery. */
  .str-chat__modal__inner:not(.fullscreen) {
    background: ${props => props.theme.brand.gradCard} !important;
    color: ${props => props.theme.brand.textPrimary};
    border: 1px solid ${props => props.theme.brand.strokeSoft};
    border-radius: 16px;
    box-shadow: ${props => props.theme.brand.shadowCard};
  }
  .str-chat__modal__close-button {
    color: ${props => props.theme.brand.textSecondary};
  }
  /* Reactions details modal (v11 emits v2 classes the v1 CSS never styles → default
     white/unstyled). Brand the type header + reactor list. */
  .str-chat__message-reactions-details {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 12px;
    min-width: 220px;
    padding: 8px 4px;
    color: ${props => props.theme.brand.textPrimary};
  }
  .str-chat__message-reactions-details-reaction-types {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    gap: 6px;
    width: 100%;
    padding-bottom: 12px;
    border-bottom: 1px solid ${props => props.theme.brand.strokeSoft};
  }
  .str-chat__message-reactions-details-reaction-type {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 4px 10px;
    border-radius: 999px;
    border: 1px solid transparent;
    font-weight: 600;
    color: ${props => props.theme.brand.textSecondary};
    cursor: pointer;
    transition: background 120ms ease;
  }
  .str-chat__message-reactions-details-reaction-type:hover {
    background: ${props => props.theme.brand.accentWash};
  }
  .str-chat__message-reactions-details-reaction-type--selected {
    background: ${props => props.theme.brand.accentWash};
    border-color: ${props => props.theme.brand.royalCobalt};
    color: ${props => props.theme.brand.textPrimary};
  }
  .str-chat__message-reaction-count {
    font-size: 13px;
  }
  .str-chat__message-reaction-emoji-big {
    font-size: 30px;
    line-height: 1;
  }
  .str-chat__message-reactions-details-reacting-users {
    display: flex;
    flex-direction: column;
    gap: 2px;
    width: 100%;
    max-height: 240px;
    overflow-y: auto;
  }
  .str-chat__message-reactions-details-reacting-user {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 6px 8px;
    border-radius: 8px;
  }
  .str-chat__message-reactions-details-reacting-user:hover {
    background: ${props => props.theme.brand.accentWash};
  }
  .str-chat__message-reactions-details-reacting-user .str-chat__user-item--name {
    color: ${props => props.theme.brand.textPrimary};
    font-size: 14px;
    font-weight: 500;
  }

  .splide {
    position: relative;
    visibility: unset;
  }

  .splide.is-initialized,
  .splide.is-rendered {
    visibility: unset;
  }
  
  .str-chat__li--top .str-chat__message .str-chat__avatar,
  .str-chat__li--middle .str-chat__message .str-chat__avatar,
  .grp-author-continues .str-chat__message .str-chat__avatar {
    visibility: hidden;
  }

@media screen and (max-width: 960px) {
.str-chat-channel-list.messaging, .str-chat-channel-list.team, .str-chat-channel-list.commerce {
    position: static;
    box-shadow: unset;
    min-height: unset;
  }
}

@media screen and (max-width: 960px) {
  .str-chat__header-hamburger {
    display: none;
  }
}

  .raf-loading-indicator {
    background: transparent !important;
    padding: 0 !important;
  }
  .raf-new-activities-notification {
    background: var(--surface-2);
    color: var(--text-primary);
    border: 1px solid var(--stroke-med);
    box-shadow: none;
  }

  /* emoji-mart v3 picker (bundled by react-activity-feed) — brand the dark mode;
     light keeps the library default. The library theme prop isn't forwarded by the
     wrapper, so the picker is themed via CSS like other third-party surfaces. */
  [data-theme='dark'] .emoji-mart {
    color: var(--text-primary);
    border-color: var(--stroke-soft);
    background-color: var(--surface-1);
  }
  [data-theme='dark'] .emoji-mart-bar {
    border-color: var(--stroke-soft);
  }
  [data-theme='dark'] .emoji-mart-search input {
    color: var(--text-primary);
    border-color: var(--stroke-med);
    background-color: var(--surface-2);
  }
  [data-theme='dark'] .emoji-mart-search-icon svg {
    fill: var(--text-tertiary);
  }
  [data-theme='dark'] .emoji-mart-category .emoji-mart-emoji:hover:before {
    background-color: var(--accent-wash);
  }
  [data-theme='dark'] .emoji-mart-category-label span {
    background-color: var(--surface-1);
    color: var(--text-secondary);
  }
  [data-theme='dark'] .emoji-mart-skin-swatches {
    border-color: var(--stroke-soft);
    background-color: var(--surface-1);
  }
  [data-theme='dark'] .emoji-mart-anchor {
    color: var(--text-tertiary);
  }
  [data-theme='dark'] .emoji-mart-anchor:hover,
  [data-theme='dark'] .emoji-mart-anchor:focus,
  [data-theme='dark'] .emoji-mart-anchor-selected {
    color: var(--accent-ink);
  }
`;
